/**
 * EmptyState Component
 *
 * Displayed when no email template is selected in the preview pane.
 * Styled to match the site's visual patterns.
 */

import { motion } from 'motion/react'
import { Mail, FolderOpen, PanelLeftOpen } from 'lucide-react'

interface EmptyStateProps {
  onOpenDrawer?: () => void
}

export function EmptyState({ onOpenDrawer }: EmptyStateProps) {
  return (
    <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-white">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center px-8"
      >
        {/* Decorative icon stack */}
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className="absolute inset-0 bg-eventus-purple/5 rounded-2xl rotate-6" />
          <div className="absolute inset-0 bg-eventus-teal/10 rounded-2xl -rotate-3" />
          <div className="relative w-full h-full bg-white rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center">
            <Mail className="w-10 h-10 text-eventus-purple/40" />
          </div>
        </div>

        <h3 className="text-lg font-semibold text-eventus-navy mb-2">Select a Template</h3>
        <p className="text-slate-500 text-sm max-w-xs mx-auto mb-4">
          Choose an email template from the sidebar to preview and edit its contents.
        </p>

        {/* Action button */}
        {onOpenDrawer && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onOpenDrawer}
            className="inline-flex items-center gap-2 px-4 py-2 bg-eventus-purple/10 hover:bg-eventus-purple/20 text-eventus-purple rounded-lg text-sm font-medium transition-colors"
          >
            <PanelLeftOpen className="w-4 h-4" />
            Open Templates
          </motion.button>
        )}

        {/* Hint */}
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full text-xs text-slate-500">
          <FolderOpen className="w-3.5 h-3.5" />
          Click a template file to preview
        </div>
      </motion.div>
    </div>
  )
}
